// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Alert,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import { useProfilingData } from '@shared/advisory/client-profile/providers';
import { Translation } from '@shared/ui-kit/providers';
import type {
  AdvisoryInstitution,
  InstitutionAccount,
} from '@shared/data-access/types/brokerage-types';
import {
  isNuvaraInversisInstitution,
  NUVARA_INVERSIS_INSTITUTION_ID,
  STATIC_INVERSIS_CUSTODY_IBAN,
} from '@shared/data-access/utils/nuvara-inversis-institution';
import type { SelectedInstitution } from '@shared/advisory/client-profile/providers';
import { Fragment, useCallback, useEffect, useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { selectClientInstitutions } from '@shared/data-access/slices/clients/clients-selectors';

interface AccountWithInstitution extends InstitutionAccount {
  institutionId: string;
  institutionName: string;
}

interface InstitutionGroup {
  id: string;
  name: string;
  accounts: AccountWithInstitution[];
}

function mapInstitutionToGroup(inst: AdvisoryInstitution): InstitutionGroup {
  return {
    id: inst.id,
    name: inst.name,
    accounts: (inst.products?.accounts ?? []).map(account => ({
      ...account,
      institutionId: inst.id,
      institutionName: inst.name,
    })),
  };
}

function buildStaticInversisGroup(): InstitutionGroup {
  return {
    id: NUVARA_INVERSIS_INSTITUTION_ID,
    name: 'Inversis',
    accounts: [
      {
        iban: STATIC_INVERSIS_CUSTODY_IBAN,
        institutionId: NUVARA_INVERSIS_INSTITUTION_ID,
        institutionName: 'Inversis',
      },
    ] as AccountWithInstitution[],
  };
}

function buildSelectedInstitutionsFromIbans(
  institutionGroups: InstitutionGroup[],
  selectedIbansSet: Set<string>,
): SelectedInstitution[] {
  return institutionGroups
    .map(g => ({
      id: g.id,
      name: g.name,
      accounts: g.accounts
        .filter(a => selectedIbansSet.has(a.iban))
        .map(a => ({ iban: a.iban, name: a.name ?? a.iban })),
    }))
    .filter(inst => inst.accounts.length > 0);
}

export const AccountsStep: FC = () => {
  const { data, updateAdvisoryData, updateStepValidity } = useProfilingData();
  const institutions = useSelector(selectClientInstitutions);

  const hasRealNuvara = useMemo(
    () => institutions.some(isNuvaraInversisInstitution),
    [institutions],
  );

  const institutionGroups = useMemo<InstitutionGroup[]>(() => {
    const fromSlice = institutions
      .filter((inst): inst is AdvisoryInstitution => inst.id !== 'ALL')
      .map(mapInstitutionToGroup);

    if (hasRealNuvara) {
      return fromSlice;
    }

    return [buildStaticInversisGroup(), ...fromSlice];
  }, [institutions, hasRealNuvara]);

  /** Source of truth: derived from provider data.selectedInstitutions */
  const selectedIbansSet = useMemo(
    () =>
      new Set(
        data.selectedInstitutions.flatMap(inst => inst.accounts.map(a => a.iban)),
      ),
    [data.selectedInstitutions],
  );

  const isIbanSelected = useCallback(
    (iban: string) => selectedIbansSet.has(iban),
    [selectedIbansSet],
  );

  const isInstitutionFullySelected = useCallback(
    (group: InstitutionGroup) =>
      group.accounts.length > 0 &&
      group.accounts.every(a => selectedIbansSet.has(a.iban)),
    [selectedIbansSet],
  );

  const isInstitutionIndeterminate = useCallback(
    (group: InstitutionGroup) => {
      const selectedCount = group.accounts.filter(a =>
        selectedIbansSet.has(a.iban),
      ).length;
      return selectedCount > 0 && selectedCount < group.accounts.length;
    },
    [selectedIbansSet],
  );

  const handleToggleIban = useCallback(
    (iban: string) => {
      const newSet = new Set(selectedIbansSet);
      if (newSet.has(iban)) {
        newSet.delete(iban);
      } else {
        newSet.add(iban);
      }
      const next = buildSelectedInstitutionsFromIbans(institutionGroups, newSet);
      updateAdvisoryData({ selectedInstitutions: next });
    },
    [selectedIbansSet, institutionGroups, updateAdvisoryData],
  );

  const handleToggleInstitution = useCallback(
    (group: InstitutionGroup) => {
      const groupIbans = group.accounts.map(a => a.iban);
      if (groupIbans.length === 0) return;

      const allSelected = groupIbans.every(iban => selectedIbansSet.has(iban));
      const newSet = new Set(selectedIbansSet);
      if (allSelected) {
        groupIbans.forEach(iban => newSet.delete(iban));
      } else {
        groupIbans.forEach(iban => newSet.add(iban));
      }
      const next = buildSelectedInstitutionsFromIbans(institutionGroups, newSet);
      updateAdvisoryData({ selectedInstitutions: next });
    },
    [selectedIbansSet, institutionGroups, updateAdvisoryData],
  );

  useEffect(() => {
    updateStepValidity('accounts-to-advise', selectedIbansSet.size > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIbansSet.size]);

  const showEmptySelectionError = selectedIbansSet.size === 0;

  return (
    <form
      id="accounts-step-form"
      className="flex flex-col gap-y-4"
      data-testid="accounts-step-form"
    >
      <div className="flex flex-col space-y-2">
        <Typography variant="h5" data-testid="title">
          <Translation i18nKey="activation-step1.title" ns="components" />
        </Typography>
        <Typography variant="body2" data-testid="description">
          <Translation i18nKey="activation-step1.description" ns="components" />
        </Typography>
      </div>

      <List
        dense
        disablePadding
        className="!max-h-[384px] !overflow-y-auto !rounded-md !border !border-gray-200 !bg-white"
        data-testid="accounts-step-list"
      >
        {institutionGroups.map(group => (
          <Fragment key={group.id}>
            <ListSubheader
              disableGutters
              className="!sticky !top-0 !z-10 !border-b !border-gray-100 !bg-white"
              data-testid={`accounts-popover-list-subheader-${group.id}`}
            >
              <ListItemButton
                onClick={() => handleToggleInstitution(group)}
                data-testid={`accounts-popover-list-subheader-${group.id}-button`}
                className="hover:!bg-section"
                disableGutters
              >
                <ListItemIcon className="!min-w-0">
                  <Checkbox
                    checked={isInstitutionFullySelected(group)}
                    indeterminate={isInstitutionIndeterminate(group)}
                    data-testid={`checkbox-${group.id}`}
                    tabIndex={-1}
                    sx={{ pointerEvents: 'none' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={group.name}
                  primaryTypographyProps={{
                    className: '!text-border-dark !font-medium !text-sm',
                  }}
                  data-testid={`label-${group.id}`}
                />
              </ListItemButton>
            </ListSubheader>
            <List dense disablePadding className="!bg-white">
              {group.accounts.map(account => (
                <ListItem
                  key={account.iban}
                  disablePadding
                  disableGutters
                  data-testid={`accounts-step-list-item-${account.iban}`}
                >
                  <ListItemButton
                    onClick={() => handleToggleIban(account.iban)}
                    className="!pl-7 hover:!bg-section"
                    disableGutters
                  >
                    <ListItemIcon className="!min-w-0">
                      <Checkbox
                        checked={isIbanSelected(account.iban)}
                        data-testid={`checkbox-iban-${account.iban}`}
                        tabIndex={-1}
                        sx={{ pointerEvents: 'none' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={account.name || account.iban}
                      primaryTypographyProps={{
                        className: '!text-primary-medium !text-sm !font-normal',
                      }}
                      data-testid={`accounts-step-account-${account.iban}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Fragment>
        ))}
      </List>

      {showEmptySelectionError && (
        <Alert
          severity="error"
          icon={false}
          className="[&_.MuiAlert-message]:!py-0"
          data-testid="accounts-step-empty-selection-error"
        >
          <Translation
            i18nKey="activation-step1.validation-select-at-least-one"
            ns="components"
          />
        </Alert>
      )}

      {selectedIbansSet.size > 0 && (
        <Alert
          severity="info"
          icon={false}
          className="!bg-alert-info-bg text-alert-info-text [&_.MuiAlert-message]:!py-0"
          data-testid="selected-accounts-alert"
        >
          <Translation
            i18nKey="activation-step1.selected-accounts"
            ns="components"
            values={{ count: selectedIbansSet.size }}
          />
        </Alert>
      )}
    </form>
  );
};
